import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import {
  PanAppState,
  PlatformEnum,
  PlatformService,
  SESSION_STORAGE_KEYS,
} from '@panamax/app-state';
import { MakePaymentPageViewModel } from '@payments/models/make-payment-page-view-model';
import {
  getFiltersAndSortInformation,
  getMakePaymentPageViewModel,
} from '@payments/selectors/payments.selector';
import { ModalFilterService } from '@payments/services/payments-modal.service';
import { PaymentsService } from '@payments/services/payments.service';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import {
  Filter,
  InvoiceStoreService,
  SortDirection,
  SortFilter,
} from '@usf/ngrx-payment';
import {
  PaymentTransactionsViewModel,
  SortFields,
  Status,
} from '@usf/payment-types';
import { Observable, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { MakePaymentAnalyticsService } from '../tracking/analytics/make-payment-analytics.service';

export enum PaymentQueryParams {
  alertName = 'alertName',
}

export enum PaymentQueryAlertNames {
  paymentPastDue = 'PYMT_PAST_DUE',
}

@Injectable({
  providedIn: 'root',
})
export class MakePaymentService implements ModalFilterService {
  readonly restrictToOpen = true;
  readonly defaultFilter = {
    fieldName: SortFields.dueDate,
    sortValue: SortDirection.ASC,
  };

  private readonly PENDING_TRANSACTION_TYPES = [
    Status.PENDING,
    Status.PROCESSING,
    Status.CANCELLING,
    Status.AUTOPAY,
  ];

  constructor(
    private store: Store,
    private panAppState: PanAppState,
    private invoiceStoreService: InvoiceStoreService,
    private makePaymentAnalytics: MakePaymentAnalyticsService,
    private paymentsService: PaymentsService,
    private router: Router,
    readonly platform: PlatformService,
    private actionSheetController: ActionSheetController,
    private popoverController: PopoverController,
  ) {}

  clearFiltersAndResetSort() {
    this.invoiceStoreService.setInvoiceFilters([], this.restrictToOpen);

    this.invoiceStoreService.setInvoiceSort(
      [
        this.defaultFilter,
        { fieldName: SortFields.transaction, sortValue: SortDirection.DESC },
      ],
      this.restrictToOpen,
    );
  }

  getFiltersAndSortInformation$(): Observable<{
    filters: Filter[];
    sort: SortFilter[];
  }> {
    return this.store.select(getFiltersAndSortInformation);
  }

  getMakePaymentsViewModel$(): Observable<MakePaymentPageViewModel> {
    return combineLatest([
      this.store.select(getMakePaymentPageViewModel),
      this.panAppState.feature$([FEATURES.split_global_payments_outage.name]),
      this.panAppState.feature$([
        FEATURES.split_user_payments_allInvoices_download.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_payments_order_number.name,
      ]),
    ]).pipe(
      map(([vm, paymentHasOutage, canShareDownload, orderNumberFlag]) => {
        const transactions = vm.selectedTransactions;

        const hasPendingTransactions =
          this.hasPendingTransactions(transactions);
        let totalPaymentAmount = null;
        if (!hasPendingTransactions && transactions?.length) {
          totalPaymentAmount = transactions.reduce(
            (acc, transaction) => acc + transaction.amountDue,
            0,
          );
        }

        return {
          ...vm,
          paymentHasOutage,
          canShareDownload,
          showOrderNumber: orderNumberFlag,
          hasPendingTransactions,
          totalPaymentAmount,
        };
      }),
    );
  }

  private hasPendingTransactions(
    transactions: PaymentTransactionsViewModel[],
  ): boolean {
    return !!transactions?.some(vm =>
      this.PENDING_TRANSACTION_TYPES.includes(vm.status),
    );
  }

  getStatusOptions(): Map<Status, string> {
    return new Map([
      [Status.OVERDUE, 'Overdue'],
      [Status.PARTIALLY_PAID, 'Partially Paid'],
      [Status.PENDING, 'Pending'],
      [Status.PROCESSING, 'Processing'],
      [Status.CANCELLING, 'Cancelling'],
      [Status.UNPAID, 'Unpaid'],
      [Status.UNUSED, 'Unused'],
      [Status.AUTOPAY, 'Autopay'],
    ]);
  }

  async presentPaymentOptions(buttons: any[], event?) {
    if (this.platform.platformType === PlatformEnum.desktop) {
      buttons.pop();
      const popover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        cssClass: 'ellipsis-options-popover-desktop',
        componentProps: {
          buttons,
        },
        mode: 'ios',
        event,
        showBackdrop: false,
      });
      return await popover.present();
    } else {
      const actionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
      await actionSheet.present();
    }
  }

  setFilter(invoiceFilters: Filter[]): void {
    this.invoiceStoreService.setInvoiceFilters(
      invoiceFilters,
      this.restrictToOpen,
    );
  }

  setInitialSortAndFilter(initialFilters: Filter[] = []) {
    this.invoiceStoreService.setInvoiceFilters(
      initialFilters,
      this.restrictToOpen,
    );
    this.invoiceStoreService.setInvoiceSort(
      [
        this.defaultFilter,
        { fieldName: SortFields.transaction, sortValue: SortDirection.DESC },
      ],
      this.restrictToOpen,
    );
  }

  setSort(sortOrder: SortFilter[]) {
    this.invoiceStoreService.setInvoiceSort(sortOrder, this.restrictToOpen);
  }

  showPaymentsOutageMessage(paymentsHasOutage: boolean) {
    if (
      paymentsHasOutage &&
      !sessionStorage.getItem(
        SESSION_STORAGE_KEYS.paymentOutageMessageDismissed_MakePaymentPage,
      )
    ) {
      this.paymentsService.showToastMessage(
        'Invoices and Payments data may not be up to date during system maintenance.',
        true,
        MessageTypeEnum.warning,
      );
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.paymentOutageMessageDismissed_MakePaymentPage,
        'true',
      );
    }
  }

  trackPageLoad() {
    this.makePaymentAnalytics.trackPageLoad();
  }

  canActivate(): Observable<boolean> {
    return combineLatest([
      this.panAppState.feature$([
        FEATURES.split_user_payments_make_a_payment.name,
      ]),
      this.panAppState.user$,
    ]).pipe(
      filter(([, user]) => !!user),
      map(([flag]) => {
        !flag && this.router.navigate([PATHS.PAYMENTS]);
        return flag;
      }),
    );
  }
}
